/* global google */
import React, { useCallback, useMemo } from 'react'
import { Marker } from '@react-google-maps/api'
import { Clusterer } from '@react-google-maps/marker-clusterer'
import useToggle from 'hooks/useToggle'
import ExperiencePins, { PinSizing } from './Pins'

function scaleDownSizing(sizing: PinSizing): PinSizing {
  const SCALE = 5 / 6
  const { size, anchor } = sizing
  return {
    size: [size[0] * SCALE, size[1] * SCALE],
    anchor: [anchor[0] * SCALE, anchor[1] * SCALE],
  }
}

interface Props {
  marker: App.SearchExperienceMapMarker;
  onClick: (marker: App.SearchExperienceMapMarker) => void;
  clusterer?: Clusterer;
  scaleDown?: boolean;
  onLoad: (markerInstance: google.maps.Marker, markerData: App.SearchExperienceMapMarker) => void;
  onUnmount?: (markerInstance: google.maps.Marker) => void;
  active?: boolean;
  visited?: boolean;
}

function SearchMapMarker(props: Props) {
  const {
    active,
    visited,
    marker,
    clusterer,
    scaleDown,
    onClick,
    onLoad,
    onUnmount,
  } = props

  const { value: hovered, on: hoverOn, off: hoverOff } = useToggle()
  // Get the first category from all the experienceItems on that marker or 999 as a fallback pin

  // eslint-disable-next-line react-hooks/exhaustive-deps -- experienceItems will never change in the lifetime of the marker
  const PinConfig = useMemo(() => ExperiencePins[marker.experienceItems.map(item => item.primaryCategory?.parentId)[0] || 999], [])
  let pinVersion = PinConfig.default
  if (active) {
    pinVersion = PinConfig.active
  } else if (visited) {
    pinVersion = PinConfig.previewed
  }

  const pin = hovered ? pinVersion.hover : pinVersion.default
  const { image, sizing: fullSizing } = pin

  const sizing = scaleDown ? scaleDownSizing(fullSizing) : fullSizing

  const iconSize = new google.maps.Size(...sizing.size)
  const anchor = new google.maps.Point(...sizing.anchor)
  const position = { lat: marker.latitude, lng: marker.longitude }
  const iconObject = { url: `${image}`, scaledSize: iconSize, anchor }

  const loadHandler = useCallback((markerInstance: google.maps.Marker) => {
    onLoad(markerInstance, marker)
  }, [marker, onLoad])
  const clickHandler = useCallback(() => {
    onClick(marker)
  }, [onClick, marker])

  return (
    <Marker
      clusterer={clusterer}
      position={position}
      icon={iconObject}
      onLoad={loadHandler}
      onUnmount={onUnmount}
      onMouseOver={hoverOn}
      onMouseOut={hoverOff}
      onClick={clickHandler}
    />
  )
}

export default React.memo(SearchMapMarker)
